import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { trpc } from '../lib/trpc';
import { CategoryPickerModal } from './CategoryPickerModal';

export const NO_CATEGORY_LABEL = 'No specific category';

export interface LifeEventCategoryFieldProps {
  /**
   * undefined = not chosen yet (invalid for expense events);
   * null = deliberate "no specific category — manual linking only".
   */
  value: number | null | undefined;
  onChange: (categoryId: number | null) => void;
  /** Mirrors the picker modal's visibility (e.g. to pause click-outside handlers). */
  onPickerOpenChange?: (open: boolean) => void;
}

/**
 * Required spending-category field for expense life events. Spending in the
 * chosen category is tracked against the event's allowance instead of the
 * recurring monthly budget.
 */
export function LifeEventCategoryField({
  value,
  onChange,
  onPickerOpenChange,
}: LifeEventCategoryFieldProps) {
  const [pickerVisible, setPickerVisible] = useState(false);
  const categoriesQ = trpc.budget.listCategories.useQuery();

  const setPickerOpen = (open: boolean) => {
    setPickerVisible(open);
    onPickerOpenChange?.(open);
  };

  const selectedName =
    value === null
      ? NO_CATEGORY_LABEL
      : value === undefined
        ? null
        : (categoriesQ.data?.find((c) => c.id === value)?.name ?? '…');

  return (
    <View className="mb-3">
      <Pressable
        testID="life-event-category-field"
        accessibilityLabel="Life event spending category"
        onPress={() => setPickerOpen(true)}
        className="border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2"
      >
        <Text
          className={
            selectedName
              ? 'text-sm text-gray-800 dark:text-gray-200'
              : 'text-sm text-gray-400 dark:text-gray-500'
          }
        >
          {selectedName ?? 'Spending category'}
        </Text>
      </Pressable>
      <Text className="text-xs text-gray-400 dark:text-gray-500 mt-1">
        Spending in this category counts toward this event instead of your
        monthly budget.
      </Text>
      <CategoryPickerModal
        visible={pickerVisible}
        onClose={() => setPickerOpen(false)}
        selectedCategoryId={value ?? null}
        onSelect={(categoryId) => {
          onChange(categoryId);
          setPickerOpen(false);
        }}
        title="Spending category"
        subtitle="Track event spending against this category"
        expenseBudgetOnly
        nullOptionLabel={NO_CATEGORY_LABEL}
      />
    </View>
  );
}
